/**
 * Utility functions to test Netlify Functions
 */

/**
 * Test the basic test function
 * @returns {Promise<Object>} The response from the test function
 */
export const testBasicFunction = async () => {
  try {
    const response = await fetch('/.netlify/functions/test');
    
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error testing basic function:', error);
    throw error;
  }
};

/**
 * Generate a valid UUID v4
 * @returns {string} A valid UUID v4 string
 */
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Test the Stripe checkout function with minimal data
 * @returns {Promise<Object>} The response from the Stripe checkout function
 */
export const testStripeCheckout = async () => {
  try {
    const testData = {
      userId: generateUUID(),
      email: 'test@example.com',
      priceId: 'price_1OvXXXXXXXXXXXXXXXXXXXXX'
    };
    
    const response = await fetch('/.netlify/functions/stripe-checkout', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(testData),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! Status: ${response.status}, Body: ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error testing Stripe checkout function:', error);
    throw error;
  }
};

/**
 * Test the subscription status function
 * @returns {Promise<Object>} The response from the subscription status function
 */
export const testSubscriptionStatus = async () => {
  try {
    const testUserId = generateUUID();
    const response = await fetch(`/.netlify/functions/subscription-status?userId=${testUserId}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! Status: ${response.status}, Body: ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error testing subscription status function:', error);
    throw error;
  }
};

/**
 * Test the webhook function
 * @returns {Promise<Object>} The response from the webhook function
 */
export const testWebhook = async () => {
  try {
    // Create a simple test event
    const testEvent = {
      type: 'test.webhook',
      data: {
        object: {
          id: 'test_id_' + Date.now(),
          created: Math.floor(Date.now() / 1000),
          test: true
        }
      }
    };
    
    const response = await fetch('/.netlify/functions/test-webhook', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'stripe-signature': 'test_signature'
      },
      body: JSON.stringify(testEvent),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! Status: ${response.status}, Body: ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error testing webhook function:', error);
    throw error;
  }
};

/**
 * Run all tests and return the results
 * @returns {Promise<Object>} The results of all tests
 */
export const runAllTests = async () => {
  const results = {
    basicFunction: null,
    stripeCheckout: null,
    subscriptionStatus: null,
    webhook: null,
    errors: []
  };
  
  try {
    results.basicFunction = await testBasicFunction();
  } catch (error) {
    results.errors.push({
      test: 'basicFunction',
      error: error.message
    });
  }
  
  try {
    results.stripeCheckout = await testStripeCheckout();
  } catch (error) {
    results.errors.push({
      test: 'stripeCheckout',
      error: error.message
    });
  }
  
  try {
    results.subscriptionStatus = await testSubscriptionStatus();
  } catch (error) {
    results.errors.push({
      test: 'subscriptionStatus',
      error: error.message
    });
  }
  
  try {
    results.webhook = await testWebhook();
  } catch (error) {
    results.errors.push({
      test: 'webhook',
      error: error.message
    });
  }
  
  return results;
};

export default {
  testBasicFunction,
  testStripeCheckout,
  testSubscriptionStatus,
  runAllTests
};
